/**
 * Event-name & event-slug hygiene for the events feed.
 *
 * Upstream event data occasionally contains junk "names" that are really
 * internal codes or unresolved placeholders rather than real event titles
 * (e.g. "TBD at TBD", "kwn", "FB0307A", "25ACCTCR"). It ALSO contains a large
 * class of NON-events: vouchers, season/flex passes, member/gift cards,
 * parking-only listings, priority-list deposits and dummy "not a game ticket"
 * rows. These are UI-visible and SEO-harmful (empty-shell / thin pages), so we
 * filter them out at the data layer before they reach any list/feed or get
 * baked into the sitemap.
 *
 * Design rule: be CONSERVATIVE. It is far worse to hide a real event than to
 * let one stray code through. Every rule below was calibrated against the live
 * events dataset (~3.2k distinct names / ~5.4k SSG slugs) so it flags only
 * clear garbage and never trips on legitimate names like "Journey",
 * "Caifanes", "BIG3", "America250", "DRAM", "BÔA", "Cardi B", the St. Louis
 * Cardinals / Arizona Cardinals / Louisville Cardinals, the Milwaukee Bucks,
 * or playoff names that legitimately contain "TBD"
 * (e.g. "Boston Legacy FC vs. TBD", where only one side is undecided).
 */

const VOWELS = /[aeiouyàâäãåáæèéêëìíîïòóôöõøùúûü]/i;

/**
 * Returns true when `name` is clearly not a real, human-facing event title and
 * should be hidden from feeds, lists, search results, and the sitemap.
 */
export function isGarbageEventName(name: string | null | undefined): boolean {
  if (name == null) return true;
  const n = name.trim();
  if (!n) return true;

  const upper = n.toUpperCase();

  // 1. Pure placeholder: a bare "TBD", or "TBD <vs/at/@> TBD" with BOTH sides
  //    unresolved. We intentionally do NOT flag names where only one side is
  //    TBD ("... vs. TBD") because those are real, scheduled playoff games.
  if (upper === 'TBD') return true;
  if (/^TBD\s*(?:AT|VS\.?|@|V|-)\s*TBD$/.test(upper)) return true;

  // 2. Single cryptic token (no whitespace) that looks like an internal code.
  if (!/\s/.test(n)) {
    // a) Letters and digits mixed in a code-like way: a letter appears AFTER a
    //    digit ("FB0307A", "25ACCTCR"). This deliberately spares word+number
    //    names like "BIG3" or "America250" where digits only trail the word.
    if (/[0-9]/.test(n) && /[0-9][A-Za-z]/.test(n)) return true;

    // b) Short, all-lowercase, vowel-less stub ("kwn"). Real lowercase artist
    //    names ("bixby") have a vowel; real vowel-less names ("DRAM") are caps.
    const letters = n.replace(/[^A-Za-z]/g, '');
    if (letters && n === n.toLowerCase() && letters.length <= 4 && !VOWELS.test(letters)) {
      return true;
    }
  }

  // 3. NON-event descriptors (vouchers / passes / member-cards / parking-only /
  //    deposits / dummy rows). These are detected from the normalized name via
  //    the shared slug-level rule so feeds, search and the sitemap agree.
  if (isGarbageEventSlug(nameToSlugish(n))) return true;

  return false;
}

/**
 * Normalize a human event NAME into a slug-ish token string so the same
 * NON-event rules apply to both names and SSG slugs. Lowercases, replaces any
 * run of non-alphanumeric chars with a single hyphen, and trims hyphens.
 */
export function nameToSlugish(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Strip the trailing date (and optional short hash) that every event slug
 * carries, so descriptor matching sees the human part only.
 *   "...-2026-03-12"        -> "..."
 *   "...-2026-03-12-6ef99a" -> "..."
 *   "game-20260301-..."     -> unchanged (leading date form; handled by token match)
 */
export function stripSlugDate(slug: string): string {
  return slug.replace(/-\d{4}-\d{2}-\d{2}(?:-[0-9a-f]{2,8})?$/i, '');
}

/**
 * Tokens that, when they appear as a WHOLE word in an event slug, mark it as a
 * NON-event (a voucher / pass / card / parking-only / deposit etc.) rather than
 * a real, indexable game or show.
 *
 * IMPORTANT — these are matched on word boundaries only, so they never trip on
 * legitimate substrings:
 *   - "card"     would hit Cardinals / Cardi B  -> NOT used; only "member-card"
 *                / "gift-card" multi-word forms below.
 *   - "bucks"    is Milwaukee Bucks             -> NOT used; only "voucher(s)".
 *   - "pass"     never appears inside another word ("compass"/"El Paso") in the
 *                dataset; every occurrence is a literal pass product (season /
 *                flex / guest / grounds / postgame / hosting / cabana pass).
 */
const JUNK_WORD_TOKENS = [
  'voucher',
  'vouchers',
  'pass',
  'passes',
  'parking', // parking-ONLY listings; a real game never has "parking" as a token
  'deposit',
  'deposits',
];

/**
 * Multi-word junk phrases (matched as contiguous hyphen-joined sequences). Used
 * for descriptors whose single tokens WOULD collide with real names ("card",
 * "membership", "appointment") but whose multi-word form is unambiguous.
 */
const JUNK_PHRASES = [
  'member-card',
  'gift-card',
  'season-seat',
  'season-flex',
  'flex-membership',
  'flex-memberships',
  'fan-group-experience',
  'fan-group-experiences',
  'season-appointment',
  'dummy-event',
  'not-a-game-ticket',
  'not-a-game',
  'digital-voucher',
];

/**
 * Returns true when an event SLUG describes a NON-event (voucher / pass /
 * member-card / parking-only / deposit / dummy row) and therefore should be
 * kept out of the sitemap, search, and feeds.
 *
 * Conservative by construction: matches only whole-word junk tokens and
 * unambiguous multi-word phrases, after stripping the trailing date.
 */
export function isGarbageEventSlug(slug: string | null | undefined): boolean {
  if (slug == null) return false;
  const s = slug.trim().toLowerCase();
  if (!s) return false;

  const core = stripSlugDate(s);
  // Split into word tokens on hyphen boundaries; also keep the joined form for
  // phrase matching.
  const tokens = core.split('-').filter(Boolean);
  const tokenSet = new Set(tokens);

  for (const t of JUNK_WORD_TOKENS) {
    if (tokenSet.has(t)) return true;
  }

  for (const phrase of JUNK_PHRASES) {
    // contiguous match: the phrase's hyphen-joined form appears in core, on
    // token boundaries.
    if (core === phrase || core.startsWith(phrase + '-') ||
        core.endsWith('-' + phrase) || core.includes('-' + phrase + '-')) {
      return true;
    }
  }

  return false;
}

/** Convenience: keep only events whose `name` is a real, displayable title. */
export function filterGarbageEvents<T extends { name?: string | null }>(events: T[]): T[] {
  return events.filter((e) => !isGarbageEventName(e?.name));
}
